import { DateTime } from "luxon";
import { pricesByBillingIntervalAndPlan } from "~/constants";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import {
  discountFromCoupon,
  priceFromCoupon,
} from "~/utils/couponCalculations";

interface PlanDiscount {
  fullPrice: number;
  discountPrice: string;
  discountedAmount: string;
}

type TrialDiscount = null | {
  activatedAt: string | null;
  discount: string | null;
  discountExpiresShort: string | null;
  hasEnded: boolean;
  endedAtLong: string | null;
  endedAtLocaleDate: string | null;
  endedAtShort: string | null;
  extendedAt: string | null;
  partnerName: string | null;
  plus: PlanDiscount;
  premium: PlanDiscount;
  weight_loss: PlanDiscount;
  upcomingPlan: string | null;
};

export const useTrialDiscount = (): TrialDiscount => {
  const currentUser = useCurrentUser();

  if (!currentUser) return null;

  const hasEnded =
    !!currentUser.partner?.endedAt &&
    DateTime.fromISO(currentUser.partner?.endedAt).diffNow().milliseconds < 0;

  if (!currentUser.partner?.name) return null;

  const interval = "1-month";

  const fullPricePremium = pricesByBillingIntervalAndPlan[interval]["premium"];
  const fullPricePlus = pricesByBillingIntervalAndPlan[interval]["plus"];
  const duration = 1;

  const activatedAt = currentUser.partner?.activatedAt;

  const endedAtLong = new Date(
    currentUser?.partner?.endedAt ?? "",
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const endedAtLocaleDate = new Date(
    currentUser?.partner?.endedAt ?? "",
  ).toLocaleDateString();

  const endedAtShort =
    (currentUser.partner?.endedAt &&
      DateTime.fromISO(currentUser.partner?.endedAt).toLocaleString({
        month: "short",
        day: "2-digit",
      })) ||
    "";

  const {
    partner: {
      name: partnerName,
      extendedAt,
      coupon: { amount, type, expires },
    },
  } = currentUser;

  const getDiscount = () => {
    if (!type) return null;

    return type === "percent" ? `${amount}%` : `$${amount}`;
  };

  const discountExpiresShort = expires
    ? DateTime.fromISO(expires).toFormat("M/dd")
    : null;

  const premiumCoupon = {
    id: "trial",
    amount_off: null,
    duration_in_months: duration,
    percent_off: amount,
  };

  const discountPricePremium = priceFromCoupon(fullPricePremium, premiumCoupon);
  const discountedAmountPremium = discountFromCoupon(
    fullPricePremium,
    premiumCoupon,
  );

  const plusCoupon = {
    id: "trial",
    amount_off: null,
    duration_in_months: duration,
    percent_off: amount,
  };
  const discountPricePlus = priceFromCoupon(fullPricePlus, plusCoupon);
  const discountedAmountPlus = discountFromCoupon(fullPricePlus, plusCoupon);

  const premium = {
    fullPrice: fullPricePremium,
    discountPrice: discountPricePremium,
    discountedAmount: discountedAmountPremium,
  };

  const plus = {
    fullPrice: fullPricePlus,
    discountPrice: discountPricePlus,
    discountedAmount: discountedAmountPlus,
  };

  const weight_loss = {
    fullPrice: fullPricePlus,
    discountPrice: discountPricePlus,
    discountedAmount: discountedAmountPlus,
  };

  const upcomingPlan = currentUser.partner?.upcomingPlan;

  return {
    activatedAt,
    discount: getDiscount(),
    discountExpiresShort,
    endedAtLong,
    endedAtLocaleDate,
    endedAtShort,
    extendedAt,
    hasEnded,
    partnerName,
    plus,
    premium,
    weight_loss,
    upcomingPlan,
  };
};
